// src/app/services/deliveryStatusService.ts
import axiosInstance from '@/app/api/axiosInstance';

export interface DeliveryStatusCounts {
  notDelivered: number; // Count of not_delivered status only
  packed: number;
  shipped: number;
  partiallyDelivered: number;
}

interface DeliveryStatusCountsApiResponse {
  not_delivered: number;
  packed: number;
  shipped: number;
  partially_delivered: number;
}

/**
 * Fetch counts for each delivery status.
 *
 * One grouped query on the server. This used to be four `listVouchers({ limit: 1 })`
 * calls made purely to read `total` off each response — on every page load, since
 * the badges live in the app header.
 *
 * @param excludePartyGroupId - when provided, invoices whose customer belongs to
 *   this party group (e.g. CORPORATE SALES) are excluded from every count. Uses
 *   the NULL-safe `ne_or_null` operator so customers with no group are retained.
 */
export const fetchDeliveryStatusCounts = async (
  excludePartyGroupId?: number
): Promise<DeliveryStatusCounts> => {
  const filters: Array<{ field: string; value: unknown; op: string }> = [
    { field: 'is_cancelled', value: false, op: 'eq' }
  ];
  if (excludePartyGroupId) {
    filters.push({ field: 'party.party_group_id', value: excludePartyGroupId, op: 'ne_or_null' });
  }

  try {
    const response = await axiosInstance.get<DeliveryStatusCountsApiResponse>(
      '/vouchers/delivery-status-counts',
      { params: { voucher_type_id: 1, filters: JSON.stringify(filters) } }
    );
    const data = response.data;

    return {
      notDelivered: data.not_delivered || 0,
      packed: data.packed || 0,
      shipped: data.shipped || 0,
      partiallyDelivered: data.partially_delivered || 0
    };
  } catch (error) {
    console.error('Error fetching delivery status counts:', error);
    // Return zeros on error to prevent UI breaking
    return {
      notDelivered: 0,
      packed: 0,
      shipped: 0,
      partiallyDelivered: 0
    };
  }
};
